import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  type AdditionalInfoFeedbackItem,
  type AdditionalInfoFaqRow,
  type AdditionalInfoMode,
  FAQ_ANSWER_MAX,
  FAQ_ANSWER_MIN,
  faqRowErrorMessage,
  INFO_MAX,
  additionalInfoEditMode,
  additionalInfoReplaceMessage,
  additionalInfoSectionHasContent,
  collectEditModeFeedbackItems,
  hasEditedAdditionalInfo,
  canAddFaqRow,
  createEmptyFaqRow,
  createInitialFaqRows,
  faqRowsAreValid,
  isFaqRowComplete,
  isFaqRowStarted,
} from './additional-info.logic';

@Component({
  selector: 'lib-additional-info-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './additional-info-edit.component.html',
  styleUrl: './additional-info.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdditionalInfoEditComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly initialFaqRows = input<AdditionalInfoFaqRow[]>([]);
  readonly initialInfoText = input('');

  readonly faqAnswerMin = FAQ_ANSWER_MIN;
  readonly faqAnswerMax = FAQ_ANSWER_MAX;
  readonly infoMax = INFO_MAX;
  faqRows: AdditionalInfoFaqRow[] = [];
  infoText = '';
  mode: AdditionalInfoMode | null = 'faq';
  pendingMode: AdditionalInfoMode | null = null;
  private loadedFaqRows: AdditionalInfoFaqRow[] = [];
  private loadedInfoText = '';
  private faqTouched = false;

  ngOnInit(): void {
    this.loadedFaqRows = this.initialFaqRows().map((row) => ({
      question: row.question,
      answer: row.answer,
    }));
    this.loadedInfoText = this.initialInfoText();
    this.mode = additionalInfoEditMode(this.loadedFaqRows, this.loadedInfoText);
    this.faqRows =
      this.loadedFaqRows.length > 0
        ? this.loadedFaqRows.map((row) => ({ ...row }))
        : createInitialFaqRows();
    this.infoText = this.loadedInfoText;
  }

  replaceMessage(next: AdditionalInfoMode): string {
    return additionalInfoReplaceMessage(next);
  }

  selectMode(next: AdditionalInfoMode): void {
    if (this.mode === next) {
      this.pendingMode = null;
      this.cdr.markForCheck();
      return;
    }
    if (this.mode && additionalInfoSectionHasContent(this.mode, this.faqRows, this.infoText)) {
      this.pendingMode = next;
      this.cdr.markForCheck();
      return;
    }
    this.applyMode(next);
  }

  confirmPendingMode(): void {
    if (!this.pendingMode) return;
    this.applyMode(this.pendingMode);
  }

  cancelPendingMode(): void {
    this.pendingMode = null;
    this.cdr.markForCheck();
  }

  private applyMode(next: AdditionalInfoMode): void {
    this.pendingMode = null;
    this.mode = next;
    if (next === 'info') {
      this.faqRows = createInitialFaqRows();
      this.faqTouched = false;
    } else {
      this.infoText = '';
      if (this.faqRows.length === 0) {
        this.faqRows = createInitialFaqRows();
      }
    }
    this.cdr.markForCheck();
  }

  get canAddMoreFaq(): boolean {
    return canAddFaqRow(this.faqRows);
  }

  addFaqRow(): void {
    if (!canAddFaqRow(this.faqRows)) {
      return;
    }
    this.faqRows = [...this.faqRows, createEmptyFaqRow()];
    this.cdr.markForCheck();
  }

  /** Any row — pre-existing or newly added — can be deleted; PUT resends the reduced list. */
  removeFaqRow(index: number): void {
    const next = this.faqRows.filter((_, i) => i !== index);
    if (next.length === 0) {
      this.mode = null;
      this.pendingMode = null;
      this.faqRows = createInitialFaqRows();
      this.faqTouched = false;
    } else {
      this.faqRows = next;
    }
    this.cdr.markForCheck();
  }

  /** Closing Information unchecks the radio. Saving then removes the stored INFO record. */
  clearInfo(): void {
    this.infoText = '';
    this.mode = null;
    this.pendingMode = null;
    this.cdr.markForCheck();
  }

  onFaqQuestionChange(index: number, value: string): void {
    const row = this.faqRows[index];
    if (!row) return;
    row.question = value;
    this.faqRows = [...this.faqRows];
    this.cdr.markForCheck();
  }

  onFaqAnswerChange(index: number, value: string): void {
    const row = this.faqRows[index];
    if (!row) return;
    row.answer = value.slice(0, FAQ_ANSWER_MAX);
    this.faqRows = [...this.faqRows];
    this.cdr.markForCheck();
  }

  onInfoTextChange(value: string): void {
    this.infoText = value.slice(0, INFO_MAX);
    this.cdr.markForCheck();
  }

  faqRowMessage(index: number): string {
    const row = this.faqRows[index];
    if (!row || !isFaqRowStarted(row)) return '';
    return faqRowErrorMessage(row);
  }

  showQuestionError(index: number): boolean {
    const row = this.faqRows[index];
    if (!row || !this.faqRowMessage(index)) return false;
    return row.question.trim().length === 0;
  }

  showAnswerError(index: number): boolean {
    const row = this.faqRows[index];
    if (!row || !this.faqRowMessage(index)) return false;
    return row.question.trim().length > 0;
  }

  validate(): boolean {
    if (this.mode !== 'faq') {
      return true;
    }
    this.faqTouched = true;
    this.cdr.markForCheck();
    return faqRowsAreValid(this.faqRows);
  }

  getChangedFeedbackItems(): AdditionalInfoFeedbackItem[] {
    if (!this.mode) {
      const items: AdditionalInfoFeedbackItem[] = [];
      if (this.loadedFaqRows.some(isFaqRowStarted)) {
        items.push({ type: 'FAQ', data: [] });
      }
      if (this.loadedInfoText.trim()) {
        items.push({ type: 'INFO', data: [] });
      }
      return items;
    }
    return collectEditModeFeedbackItems(
      this.mode,
      this.faqRows,
      this.loadedFaqRows,
      this.infoText,
      this.loadedInfoText,
    );
  }

  /**
   * Save stays off until the section on screen has text, unless Remove and
   * continue already cleared a section that was loaded from the server.
   */
  get hasChanges(): boolean {
    if (!this.mode) {
      return this.loadedFaqRows.some(isFaqRowStarted) || this.loadedInfoText.trim().length > 0;
    }
    if (
      this.mode === 'info' &&
      !this.infoText.trim() &&
      this.faqRows.some(isFaqRowStarted)
    ) {
      return false;
    }
    if (
      this.mode === 'faq' &&
      !this.faqRows.some(isFaqRowStarted) &&
      !!this.infoText.trim()
    ) {
      return false;
    }
    return hasEditedAdditionalInfo(
      this.faqRows,
      this.loadedFaqRows,
      this.infoText,
      this.loadedInfoText,
    );
  }
}
